export const OB_SOURCE_TRIGGER_REASONS = new Set([
  'initial_lock',
  'relock',
  'lock_extension',
  'pricing_change'
]);

export const SELECTABLE_LOCK_TRIGGER_REASONS = new Set([
  ...OB_SOURCE_TRIGGER_REASONS
]);

export interface MaterialSnapshotLike {
  id: string;
  version?: number | null;
  materialLockHash?: string | null;
  triggerReason?: string | null;
}

export interface MaterialLockRun<TSnapshot extends MaterialSnapshotLike> {
  id: string;
  hash: string | null;
  snapshots: TSnapshot[];
  displayRepresentative: TSnapshot;
  setActiveRepresentative: TSnapshot;
  activeSnapshot: TSnapshot | null;
  representative: TSnapshot;
  newest: TSnapshot;
  hasActive: boolean;
  hasObSource: boolean;
}

export function isObSourcedSnapshot(snapshot: MaterialSnapshotLike): boolean {
  return snapshot.triggerReason ? OB_SOURCE_TRIGGER_REASONS.has(snapshot.triggerReason) : false;
}

export function isSelectableLockVersion(snapshot: MaterialSnapshotLike): boolean {
  return snapshot.triggerReason ? SELECTABLE_LOCK_TRIGGER_REASONS.has(snapshot.triggerReason) : false;
}

export const isSelectableLockSnapshot = isSelectableLockVersion;

export function isManualCaptureLockVersion(snapshot: MaterialSnapshotLike): boolean {
  return snapshot.triggerReason === 'manual_capture';
}

function snapshotHash(snapshot: MaterialSnapshotLike): string | null {
  return snapshot.materialLockHash ?? null;
}

function sortByVersionAsc(a: MaterialSnapshotLike, b: MaterialSnapshotLike): number {
  return (a.version ?? 0) - (b.version ?? 0);
}

export function buildMaterialRuns<TSnapshot extends MaterialSnapshotLike>(
  snapshots: TSnapshot[],
  activeSnapshotId: string | null | undefined
): Array<MaterialLockRun<TSnapshot>> {
  const sorted = [...snapshots].sort(sortByVersionAsc);
  const groups: TSnapshot[][] = [];

  for (const snapshot of sorted) {
    const hash = snapshotHash(snapshot);
    const prev = groups[groups.length - 1];
    const prevHash = prev ? snapshotHash(prev[prev.length - 1]) : null;
    if (prev && hash && prevHash && hash === prevHash) {
      prev.push(snapshot);
    } else {
      groups.push([snapshot]);
    }
  }

  return groups.map((group, index) => {
    const oldest = group[0];
    const newest = group[group.length - 1];
    const active = activeSnapshotId
      ? group.find((snapshot) => snapshot.id === activeSnapshotId) ?? null
      : null;
    const oldestNormalSelectable = group.find(isSelectableLockVersion) ?? null;
    const newestNormalSelectable = [...group].reverse().find(isSelectableLockVersion) ?? null;
    const displayRepresentative = oldestNormalSelectable ?? oldest;
    const setActiveRepresentative = newestNormalSelectable ?? newest;
    return {
      id: `${snapshotHash(newest) ?? newest.id}:${index}`,
      hash: snapshotHash(newest),
      snapshots: group,
      displayRepresentative,
      setActiveRepresentative,
      activeSnapshot: active,
      representative: displayRepresentative,
      newest,
      hasActive: !!active,
      hasObSource: group.some(isObSourcedSnapshot)
    };
  }).reverse();
}

export function isReviewableMaterialRun<TSnapshot extends MaterialSnapshotLike>(
  run: MaterialLockRun<TSnapshot>
): boolean {
  const manualOnly = run.snapshots.every(isManualCaptureLockVersion);
  return !manualOnly || run.hasActive;
}

export function getReviewableMaterialRuns<TSnapshot extends MaterialSnapshotLike>(
  snapshots: TSnapshot[],
  activeSnapshotId: string | null | undefined
): Array<MaterialLockRun<TSnapshot>> {
  return buildMaterialRuns(snapshots, activeSnapshotId).filter(isReviewableMaterialRun);
}

export function findLiveMatchingRun<TSnapshot extends MaterialSnapshotLike>(
  runs: Array<MaterialLockRun<TSnapshot>>,
  liveMaterialLockHash: string | null | undefined
): MaterialLockRun<TSnapshot> | null {
  if (!liveMaterialLockHash) {
    return null;
  }
  const latestRun = runs[0] ?? null;
  return latestRun?.hash === liveMaterialLockHash ? latestRun : null;
}

export function findCurrentObRun<TSnapshot extends MaterialSnapshotLike>(
  runs: Array<MaterialLockRun<TSnapshot>>,
  liveMaterialLockHash: string | null | undefined
): MaterialLockRun<TSnapshot> | null {
  const latestLiveMatchingRun = findLiveMatchingRun(runs, liveMaterialLockHash);
  return latestLiveMatchingRun?.hasObSource ? latestLiveMatchingRun : null;
}

export function hasNewerMaterialRun<TSnapshot extends MaterialSnapshotLike>(
  snapshots: TSnapshot[],
  activeSnapshotId: string | null | undefined
): boolean {
  if (!activeSnapshotId) {
    return false;
  }
  const runs = getReviewableMaterialRuns(snapshots, activeSnapshotId);
  const activeRunIndex = runs.findIndex((run) => run.hasActive);
  if (activeRunIndex < 0) {
    return false;
  }
  // Runs are newest-first. Any row before the active run is a newer material run.
  return activeRunIndex > 0;
}
